import { useEffect, useState } from "react";
import { competitionAPI } from "../services/api";

// Single source of truth for "are submissions open?".
// Input = ONLY the active competition payload from GET /api/competitions/active.
// No hardcoded date, no flag, no env var, no build-time snapshot.
// Fail-safe = CLOSED: loading, network error, no active competition, or an
// absent/unparsable deadline all resolve to open=false.

// Shared cache: one fetch for the whole app (all consumers reuse this promise).
let cachedPromise = null;
const fetchActiveCompetition = () => {
  if (!cachedPromise) {
    cachedPromise = competitionAPI
      .getActiveCompetition()
      .then((res) => res.data)
      .catch(() => null);
  }
  return cachedPromise;
};

// Parse the deadline as an absolute instant.
// - String WITH offset (Z or +hh:mm / -hh:mm) → used as-is.
// - Naive string (no offset, e.g. "2026-05-07T15:59:59") → interpreted as UTC.
const parseDeadlineMs = (raw) => {
  if (!raw || typeof raw !== "string") return null;
  const s = raw.trim().replace(" ", "T");
  const hasOffset = /Z$/i.test(s) || /[+-]\d{2}:?\d{2}$/.test(s);
  const ms = new Date(hasOffset ? s : `${s}Z`).getTime();
  return Number.isNaN(ms) ? null : ms;
};

export function useSubmissionsOpen() {
  const [state, setState] = useState({ loading: true, open: false });

  useEffect(() => {
    let mounted = true;
    fetchActiveCompetition().then((comp) => {
      if (!mounted) return;
      if (!comp || comp.is_active !== true) {
        setState({ loading: false, open: false });
        return;
      }
      const deadlineMs = parseDeadlineMs(comp.submission_deadline);
      if (deadlineMs === null) {
        setState({ loading: false, open: false });
        return;
      }
      // Inclusive of the deadline instant (aligns with backend: refuse when now > deadline).
      setState({ loading: false, open: Date.now() <= deadlineMs });
    });
    return () => { mounted = false; };
  }, []);

  return state;
}

export default useSubmissionsOpen;
